import React, { useState } from 'react';
import { Users, ArrowRight, CheckCircle, BarChart2, Sparkles, Zap } from 'lucide-react';
import { MarketplaceListing } from '../../types/marketplace';
import { isWhiteLabel } from '../../config/tenantConfig';
import { CHIP_ACCENT, CHIP_CATEGORY, CHIP_RISK } from '../../utils/chipColors';
import { formatTradingType } from '../../utils/scheduleSummary';

const fmtSigned = (n: number | undefined, suffix = '') =>
  n === undefined ? '—' : `${n >= 0 ? '+' : ''}${n}${suffix}`;

interface MarketplaceBasketCardProps {
  listing: MarketplaceListing;
  onClick: () => void;
  isAuthenticated?: boolean;
  isSubscribed?: boolean;
  onSubscribe?: () => void;
  /** Navigate to the My Subscriptions page (shown when already subscribed). */
  onManage?: () => void;
  /** When provided, renders a "Register" button on the card footer (Zebu event lead capture). */
  onRegister?: () => void;
}

const categoryColors: Record<string, string> = {
  INCOME: '#059669',
  MOMENTUM: '#2563eb',
  HEDGED: '#f59e0b',
  AGGRESSIVE: '#9333ea',
  CONSERVATIVE: '#10b981',
};

// Marketplace category + risk chips — sourced from canonical CHIP_CATEGORY /
// CHIP_RISK palettes so all marketplace surfaces share one color language.
const categoryBadgeColors: Record<string, string> = {
  INCOME:       CHIP_CATEGORY.INCOME,
  MOMENTUM:     CHIP_CATEGORY.MOMENTUM,
  HEDGED:       CHIP_CATEGORY.HEDGED,
  AGGRESSIVE:   CHIP_CATEGORY.AGGRESSIVE,
  CONSERVATIVE: CHIP_CATEGORY.CONSERVATIVE,
};

const riskBadgeColors: Record<string, string> = {
  LOW:    CHIP_RISK.LOW,
  MEDIUM: CHIP_RISK.MEDIUM,
  HIGH:   CHIP_RISK.HIGH,
};

const MarketplaceBasketCard: React.FC<MarketplaceBasketCardProps> = ({ listing, onClick, isAuthenticated, isSubscribed, onSubscribe, onManage, onRegister }) => {
  const wl = isWhiteLabel();
  const accentText = wl ? 'text-zebu-600 dark:text-zebu-400' : 'text-blue-600 dark:text-blue-400';
  const accentBtn = wl ? 'bg-zebu-600 hover:bg-zebu-700' : 'bg-blue-600 hover:bg-blue-700';
  const accentRing = wl ? 'focus:ring-zebu-500' : 'focus:ring-blue-500';
  const accentReturnColor = `${wl ? 'text-zebu-600 dark:text-zebu-400' : 'text-blue-600 dark:text-blue-400'}`;
  const stripColor = categoryColors[listing.category] || categoryColors.INCOME;
  const cagr = listing.backtest_cagr;
  const totalReturn = listing.backtest_total_return;
  const winRate = listing.backtest_win_rate;
  const sharpe = listing.backtest_sharpe_ratio;
  const hasBacktest =
    cagr !== undefined || totalReturn !== undefined || winRate !== undefined || sharpe !== undefined;
  // Regulation compliance: backtest numbers are not shown by default —
  // user must click through to see historical performance.
  const [showBacktest, setShowBacktest] = useState(false);

  // Masked string shown in place of real numbers when the user hasn't
  // clicked the reveal toggle yet. Using bullets keeps the card occupying
  // the same height in both states, so the grid stays aligned.
  const masked = '•••';

  return (
    <button
      type="button"
      onClick={onClick}
      style={{ borderTopColor: stripColor, borderTopWidth: '4px' }}
      className={`w-full text-left bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 transition-all hover:shadow-lg ${wl ? 'hover:border-zebu-300 dark:hover:border-zebu-700' : 'hover:border-blue-300 dark:hover:border-blue-700'} hover:-translate-y-0.5 focus:outline-none focus:ring-2 ${accentRing} flex flex-col h-full`}
    >
      <div className="p-5 flex flex-col flex-1">
        {/* Category + Risk + Trading-type badges.
            Trading-type chip uses the same `CHIP_ACCENT.TRADING_TYPE`
            cyan palette as MarketplaceDetailPage + StrategyRowView for
            cross-surface consistency. Reads strategies_preview[0] (the
            primary strategy) — same scope decision as the detail page. */}
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${categoryBadgeColors[listing.category] || categoryBadgeColors.INCOME}`}>
            {listing.category}
          </span>
          <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${riskBadgeColors[listing.risk_level] || riskBadgeColors.MEDIUM}`}>
            {listing.risk_level}
          </span>
          {(() => {
            const primary = listing.strategies_preview?.[0];
            if (!primary?.trading_type) return null;
            const label = formatTradingType(
              primary.trading_type,
              primary.intraday_exit_mode,
              primary.entry_trading_days_before_expiry,
              primary.exit_trading_days_before_expiry,
            );
            if (!label) return null;
            return (
              <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${CHIP_ACCENT.TRADING_TYPE}`}>
                {label}
              </span>
            );
          })()}
        </div>

        {/* Title — clamped to 2 lines with a fixed min-height so every card
            reserves the same vertical space regardless of name length. */}
        <h3 className="text-base font-bold text-gray-900 dark:text-white leading-tight mb-2 line-clamp-2 min-h-[2.5rem]">
          {listing.title}
        </h3>

        {/* Description — clamped likewise for consistent card heights. */}
        <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2 min-h-[2.5rem] mb-3">
          {listing.description}
        </p>

        {/* Capital — required-to-trade figure, always visible. */}
        <div className="grid grid-cols-2 gap-2 border-t border-gray-100 dark:border-gray-700 pt-3 mb-3">
          <div>
            <p className="text-[0.6rem] text-gray-400 dark:text-gray-500 uppercase">Capital</p>
            <p className="text-xs font-semibold text-gray-900 dark:text-white">
              {listing.capital_required
                ? `₹${(listing.capital_required / 1000).toFixed(0)}K`
                : '₹500K'}
            </p>
          </div>
        </div>

        {/* Backtest — always rendered in the same slot so card heights stay
            aligned. Values are masked (•••) until the user clicks the
            reveal toggle, per regulatory compliance. */}
        <div className="mb-3 rounded-lg border border-gray-200 dark:border-gray-700 p-3 bg-gray-50 dark:bg-gray-900/40">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[0.6rem] text-gray-500 dark:text-gray-400 uppercase tracking-wider font-semibold">
              Backtest Results
            </p>
            {hasBacktest && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowBacktest((v) => !v);
                }}
                className={`inline-flex items-center gap-1 text-[10px] font-medium ${accentText} hover:underline`}
                aria-label={showBacktest ? 'Hide backtest results' : 'Show backtest results'}
              >
                <BarChart2 className="h-3 w-3" />
                {showBacktest ? 'Hide' : 'Show'}
              </button>
            )}
          </div>

          {/* CAGR highlight */}
          <div className="mb-3">
            <p className="text-[0.65rem] text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-0.5">CAGR</p>
            {showBacktest && cagr !== undefined ? (
              <p className={`text-2xl font-black tabular-nums ${cagr >= 0 ? accentReturnColor : 'text-red-600 dark:text-red-400'}`}>
                {fmtSigned(cagr, '%')}
              </p>
            ) : (
              <p className="text-2xl font-black tabular-nums text-gray-300 dark:text-gray-600 select-none">{masked}</p>
            )}
          </div>

          {/* Return / Win Rate / Sharpe */}
          <div className="grid grid-cols-3 gap-2 border-t border-gray-100 dark:border-gray-700 pt-3">
            <div>
              <p className="text-[0.6rem] text-gray-400 dark:text-gray-500 uppercase">Return</p>
              <p className="text-xs font-semibold text-gray-900 dark:text-white tabular-nums">
                {showBacktest && totalReturn !== undefined
                  ? fmtSigned(totalReturn, '%')
                  : <span className="text-gray-300 dark:text-gray-600 select-none">{masked}</span>}
              </p>
            </div>
            <div>
              <p className="text-[0.6rem] text-gray-400 dark:text-gray-500 uppercase">Win Rate</p>
              <p className="text-xs font-semibold text-gray-900 dark:text-white tabular-nums">
                {showBacktest && winRate !== undefined
                  ? `${winRate}%`
                  : <span className="text-gray-300 dark:text-gray-600 select-none">{masked}</span>}
              </p>
            </div>
            <div>
              <p className="text-[0.6rem] text-gray-400 dark:text-gray-500 uppercase">Sharpe</p>
              <p className="text-xs font-semibold text-gray-900 dark:text-white tabular-nums">
                {showBacktest && sharpe !== undefined
                  ? sharpe
                  : <span className="text-gray-300 dark:text-gray-600 select-none">{masked}</span>}
              </p>
            </div>
          </div>
        </div>

        {/* Tags */}
        {listing.tags && listing.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {listing.tags.slice(0, 3).map((tag) => (
              <span key={tag} className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Footer — pinned to card bottom via mt-auto so every card's footer
            aligns across the row. */}
        <div className="flex items-center justify-between mt-auto pt-2">
          <span className="flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500">
            <Users className="h-3.5 w-3.5" />
            {listing.subscriber_count} subscribers
          </span>
          {isAuthenticated && isSubscribed ? (
            <div className="flex items-center gap-2">
              <span className={`flex items-center gap-1 text-xs font-semibold ${accentText}`}>
                <CheckCircle className="h-3.5 w-3.5" />
                Subscribed
              </span>
              {onManage && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onManage();
                  }}
                  className="text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                >
                  Manage
                </button>
              )}
            </div>
          ) : onRegister ? (
            <div className="flex items-center gap-2">
              <span className={`inline-flex items-center gap-1 text-xs font-semibold ${accentText} group-hover:underline`}>
                Details
                <ArrowRight className="h-3.5 w-3.5" />
              </span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onRegister();
                }}
                className={`inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg text-white ${accentBtn} focus:outline-none focus:ring-2 ${accentRing}`}
              >
                <Sparkles className="h-3.5 w-3.5" />
                Register
              </button>
            </div>
          ) : onSubscribe ? (
            <div className="flex items-center gap-2">
              <span className={`inline-flex items-center gap-1 text-xs font-semibold ${accentText} group-hover:underline`}>
                View Details
                <ArrowRight className="h-3.5 w-3.5" />
              </span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onSubscribe();
                }}
                className={`inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg text-white ${accentBtn} focus:outline-none focus:ring-2 ${accentRing}`}
              >
                <Zap className="h-3.5 w-3.5" />
                Subscribe
              </button>
            </div>
          ) : (
            <span className={`flex items-center gap-1 text-xs font-semibold ${accentText}`}>
              View Details
              <ArrowRight className="h-3.5 w-3.5" />
            </span>
          )}
        </div>
      </div>
    </button>
  );
};

export default MarketplaceBasketCard;
